import React, { useState, useEffect } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import ChurchLogo from './ChurchLogo';
import NotificationBell from './NotificationBell';
import { useLanguage } from '../context/LanguageContext';

const LANGUAGES = [
  { code: 'de', label: 'DE', flag: '🇩🇪' },
  { code: 'en', label: 'EN', flag: '🇬🇧' },
  { code: 'fr', label: 'FR', flag: '🇫🇷' },
];

const Header = () => {
  const { lang, switchLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [logoVariant, setLogoVariant] = useState('yellow-blue');

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    const savedVariant = localStorage.getItem('icg-church-logo-variant');
    if (savedVariant) setLogoVariant(savedVariant);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { id: 'about', label: t('nav.about') },
    { id: 'events', label: t('nav.events') },
    { id: 'visit', label: t('nav.visit') },
    { id: 'youth', label: t('nav.youth') },
    { id: 'sermons', label: t('nav.sermons') },
    { id: 'contact', label: t('nav.contact') },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button
            onClick={() => scrollToSection('home')}
            className="flex items-center gap-3 group hover:scale-105 transition-transform"
          >
            <div className={`${isScrolled ? 'bg-white shadow-md' : 'bg-white/10 backdrop-blur-sm'} p-2 rounded-xl transition-all`}>
              <ChurchLogo variant={logoVariant} size="small" />
            </div>
            <div className="text-left">
              <div className={`font-bold text-lg leading-tight transition-colors ${
                isScrolled ? 'text-gray-900' : 'text-white drop-shadow-lg'
              }`}>
                ICG Hannover
              </div>
              <div className={`text-xs transition-colors ${
                isScrolled ? 'text-gray-600' : 'text-white/90 drop-shadow'
              }`}>
                Internationale Christengemeinde
              </div>
            </div>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                data-testid={`nav-${item.id}`}
                className={`font-medium text-sm transition-all hover:scale-105 ${
                  isScrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white hover:text-blue-200'
                }`}
              >
                {item.label}
              </button>
            ))}

            {/* Notification Bell */}
            <NotificationBell />

            {/* Members Area Link */}
            <a
              href="/mitglieder/login"
              data-testid="members-link"
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
                isScrolled
                  ? 'bg-[#0f2440] text-white hover:bg-[#1a3a5c]'
                  : 'bg-white/15 text-white hover:bg-white/25'
              }`}
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              {lang === 'de' ? 'Mitglieder' : lang === 'fr' ? 'Membres' : 'Members'}
            </a>

            {/* Language Switcher */}
            <div className="relative ml-2">
              <button
                onClick={() => setShowLangDropdown(!showLangDropdown)}
                data-testid="lang-switcher"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  isScrolled
                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    : 'bg-white/15 text-white hover:bg-white/25'
                }`}
              >
                <Globe className="w-3.5 h-3.5" />
                {lang.toUpperCase()}
              </button>
              {showLangDropdown && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl border overflow-hidden min-w-[120px]">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { switchLanguage(l.code); setShowLangDropdown(false); }}
                      data-testid={`lang-${l.code}`}
                      className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-blue-50 transition-colors ${
                        lang === l.code ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'
                      }`}
                    >
                      <span>{l.flag}</span>
                      <span>{l.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Mobile: Language + Menu */}
          <div className="lg:hidden flex items-center gap-2">
            <button
              onClick={() => {
                const langs = ['de', 'en', 'fr'];
                const next = langs[(langs.indexOf(lang) + 1) % langs.length];
                switchLanguage(next);
              }}
              className={`px-2.5 py-1.5 rounded-full text-xs font-bold transition-colors ${
                isScrolled ? 'bg-gray-100 text-gray-700' : 'bg-white/15 text-white'
              }`}
            >
              {lang === 'de' ? '🇬🇧 EN' : lang === 'en' ? '🇫🇷 FR' : '🇩🇪 DE'}
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 rounded-lg transition-colors ${
                isScrolled ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white/10'
              }`}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-xl border-t">
            <nav className="flex flex-col py-4">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-left px-6 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
